import { yupResolver } from '@hookform/resolvers/yup'
import React, { forwardRef, useCallback, useImperativeHandle } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { Box, Dialog, DialogContent, DialogTitle, TextField } from '@mui/material'
import { ActionButton } from '@emerald-works-nova/components'
import { useConfirm } from 'material-ui-confirm'

const sessionFormSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  description: yup.string().required('Description is required')
})

const SessionFormDialog = ({
  session,
  onSubmit,
  isLoading,
  title,
  submitText = 'Save'
}, ref) => {
  const form = useForm({
    resolver: yupResolver(sessionFormSchema),
    mode: 'all',
    defaultValues: {
      ...session
    }
  })

  const confirm = useConfirm()
  const [open, setOpen] = React.useState(false)

  const onCancel = useCallback(() => {
    if (!form.formState.isDirty) {
      setOpen(false)
      return
    }

    confirm({
      title: 'Discard changes',
      description: 'Are you sure you want to discard the changes?',
      confirmationText: 'Yes, discard',
      cancellationText: 'No, thanks',
      confirmationButtonProps: {
        id: 'session-form-discard-confirmation-button',
        'data-test': 'session-form-discard-confirmation-button'
      },
      cancellationButtonProps: {
        id: 'session-form-discard-cancellation-button',
        'data-test': 'session-form-discard-cancellation-button'
      },
      dialogProps: {
        PaperProps: {
          id: 'session-form-discard-confirmation-modal',
          'data-test': 'session-form-discard-confirmation-modal'
        }
      },
      titleProps: {
        id: 'session-form-discard-confirmation-title',
        'data-test': 'session-form-discard-confirmation-title'
      }
    }).then(() => {
      setOpen(false)
    }).catch(() => { })
  }, [confirm, form.formState.isDirty])

  const onClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  useImperativeHandle(ref, () => ({
    open: (formDefaultValues = { name: '', description: '' }) => {
      form.reset(formDefaultValues)
      setOpen(true)
    },
    close: () => onClose()
  }), [form, onClose])

  return (
    <Dialog
      open={open}
      // Stop propagation to avoid closing the outter accordion
      onClick={(evt) => evt.stopPropagation()}
      onFocus={(evt) => evt.stopPropagation()}
      PaperProps={{
        sx: {
          minWidth: '900px'
        }
      }}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogTitle>
          <Box display='flex' flexDirection='row' justifyItems='center' justifyContent='space-between'>
            {title}
            <Box display='flex' flexDirection='row' gap={2}>
              <ActionButton
                disabled={isLoading}
                onClick={onCancel}
              >
                Cancel
              </ActionButton>
              <ActionButton
                type='submit'
                isWorking={isLoading}
                variant='primary'
                disabled={!form.formState.isValid}
              >
                {submitText}
              </ActionButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box pt={2} pb={2}>
            <Controller
              name='name'
              control={form.control}
              defaultValue={session?.name || ''}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  sx={{ width: '100%' }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  label='Session Name'
                />
              )}
            />

            <Controller
              name='description'
              control={form.control}
              defaultValue={session?.name || ''}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  sx={{ width: '100%', mt: 2 }}
                  label='Session Description'
                  multiline
                  rows={4}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Box>
        </DialogContent>
      </form>
    </Dialog>
  )
}

const SessionFormDialogWithRef = forwardRef(SessionFormDialog)
export default SessionFormDialogWithRef
